import { useCallback } from "react";

import Icon from "app/components/shared/Icon";
import useCopyToClipboard from "../analytics/shared/hooks/useCopyToClipboard";
import classNames from "classnames";

interface CopyButtonProps {
  contentToCopy: string;
  iconOnly?: boolean;
  children?: React.ReactNode;
  className?: string;
}

export default function CopyButton({
  contentToCopy,
  iconOnly,
  children,
  className,
}: CopyButtonProps) {
  const { copied, handleCopy } = useCopyToClipboard();

  const handleCopyClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.currentTarget.blur();
      handleCopy(contentToCopy);
    },
    [contentToCopy, handleCopy],
  );

  if (!navigator.clipboard || !window.isSecureContext) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleCopyClick}
      aria-label={iconOnly && copied ? "Copied" : "Copy"}
      className={classNames(
        "inline-flex h-6 items-center justify-center gap-1 rounded hover:bg-slate-800/5",
        iconOnly ? "w-6" : "px-1",
        className,
      )}
    >
      {copied ? (
        <Icon icon="heroicons/16/solid/check" className="block size-4 text-green-700" />
      ) : (
        <Icon icon="heroicons/outline/square-2-stack" className="block size-5" />
      )}

      {!iconOnly && <span>{copied ? "Copied" : children}</span>}
    </button>
  );
}
